import type {UserSessionState} from "@stores/UserSessionState";

import { httpLikeRequestTroughWebsocket } from "@stores/serverConnection/makeApiServerRequest";
import { SubscriptionSeatsService } from "../shared/services/SubscriptionSeatsService";
import { getPaddlePriceIds } from "../utils/priceIds";
import {WsServerConnection} from "@stores/serverConnection/wsServerConnection";


/**
 * Simple seat management - handles UI gating use case:
 * Can I add a user/location?
 */

export interface SeatUsage {
    users: {
        current: number;
        limit: number;
        enforceLimit: boolean;
    };
    locations: {
        current: number;
        limit: number;
        enforceLimit: boolean;
    };
}

// Plan configurations - single source of truth
const PLAN_CONFIGS = {
    freemium: {
        baseLocations: 1,
        baseUsers: 0,
        usersPerLocation: 5,
        enforceLimit: true
    },
    free_trial: {
        baseLocations: 0,
        baseUsers: 0,
        usersPerLocation: 5,
        enforceLimit: false // unlimited during trial
    },
    standard: {
        baseLocations: 0,
        baseUsers: 0,
        usersPerLocation: 5,
        enforceLimit: true
    }
} as const;

export class SeatManager {

    /**
     * Get current seat usage and limits for UI display and gating
     */
    static async getSeatUsage(userState: UserSessionState, atLeastOne: boolean = false): Promise<SeatUsage> {
        const currentClient = userState.dsManager?.clientAR?.row;
        if (!currentClient) {
            throw new Error('Client not available');
        }

        // Get current usage
        const usersDS = userState.usersDS!;
        const currentUsers = usersDS.activeUsers?.length || 0;
        const currentLocations = await this.countLocations(userState.wsServerConnection, atLeastOne);

        // Get plan config
        const planConfig = this.getPlanConfig(currentClient.subscription_plan);

        // Get purchased seats
        const purchasedSeats = SubscriptionSeatsService.getPurchasedSeats(currentClient, getPaddlePriceIds());

        // Calculate limits
        const locationLimit = planConfig.baseLocations + purchasedSeats.locationSlots

        const userLimit = planConfig.baseUsers + (locationLimit * planConfig.usersPerLocation) + purchasedSeats.userSeats

        return {
            locations: {
                current: currentLocations,
                limit: locationLimit,
                enforceLimit: planConfig.enforceLimit
            },
            users: {
                current: currentUsers,
                limit: userLimit,
                enforceLimit: planConfig.enforceLimit
            },
        };
    }

    // Helper methods
    private static async countLocations(wsServerConnection: WsServerConnection, atLeastOne: boolean = false): Promise<number> {
        const result = await httpLikeRequestTroughWebsocket(
            wsServerConnection,
            'l/count',
            'location_count',
            {},
            () => 'location_count',
            undefined,
            10000
        );
        return Math.max(result.count, atLeastOne ? 1 : 0);
    }

    public static getPlanConfig(plan?: string | null) {
        if (!plan) return PLAN_CONFIGS.freemium;

        if (plan.startsWith('free_trial')) return PLAN_CONFIGS.free_trial;
        if (plan === 'standard' || plan === 'std') return PLAN_CONFIGS.standard;
        if (plan === 'freemium') return PLAN_CONFIGS.freemium;

        return PLAN_CONFIGS.freemium; // default
    }
}